/**
 * Rich text written by engineers and managers, cleaned before it is rendered.
 *
 * Recovery updates, management updates, runbook instructions and team email templates come from
 * the TipTap editor (src/components/ui/rich-text-editor.tsx) and are rendered as raw HTML. They
 * were rendered exactly as stored, so a <script>, an onerror= handler or a javascript: link posted
 * by one person ran for everyone who opened the recovery (stored XSS, found 24 Sep 2026). The
 * backend now cleans what it stores (backend/src/lib/rich-text.js), but rows written before that
 * are still in the database, and other tables reach these screens too - so every render cleans.
 *
 * The allow-list is what the editor emits and nothing more, and it matches the backend's list
 * exactly (a test holds the two to the same content).
 *
 * This uses its OWN DOMPurify instance: the hooks that enforce per-tag attributes, classes and URL
 * schemes must not leak into the other DOMPurify users (the AI chat and the IaC page), which
 * sanitise markdown output with the library's defaults.
 */
import createDOMPurify, { type DOMPurify } from 'dompurify'

export const RICH_TEXT_ALLOWED_TAGS = [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'code', 'pre', 'blockquote',
    'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'hr', 'a', 'img',
] as const

// Only the classes the editor itself applies, and the runbook "nothing selected" placeholder's.
// An arbitrary class is not harmless: a pasted "fixed inset-0 z-50" would lay an update over the page.
export const RICH_TEXT_ALLOWED_CLASSES: Record<string, readonly string[]> = {
    a: ['text-primary', 'underline'],
    img: ['max-w-full', 'h-auto', 'rounded'],
    p: ['text-muted-foreground'],
}

export const RICH_TEXT_ALLOWED_ATTRIBUTES: Record<string, readonly string[]> = {
    a: ['href', 'target', 'rel', 'class'],
    img: ['src', 'alt', 'title', 'class'],
    p: ['class'],
}

export const RICH_TEXT_LINK_SCHEMES = ['http', 'https', 'mailto'] as const
export const RICH_TEXT_IMAGE_SCHEMES = ['http', 'https'] as const
export const RICH_TEXT_LINK_REL = 'noopener noreferrer nofollow'

const SCHEME = /^([a-z][a-z0-9+.-]*):/i

/**
 * A URL is allowed when it names one of the schemes, or is relative to this site (uploaded images
 * are stored as /api/images/... paths). Protocol-relative "//host" is not relative to this site.
 */
function urlAllowed(value: string, schemes: readonly string[]): boolean {
    // Browsers ignore whitespace and control characters inside a scheme ("java\tscript:").
    const url = Array.from(value).filter((ch) => {
        const code = ch.charCodeAt(0)
        return code > 0x20 && code !== 0x7f
    }).join('')
    if (url.startsWith('//')) return false
    const scheme = url.match(SCHEME)
    if (!scheme) return true
    return schemes.includes(scheme[1].toLowerCase())
}

let purifier: DOMPurify | null = null

function getPurifier(): DOMPurify | null {
    if (purifier) return purifier
    if (typeof window === 'undefined') return null
    const instance = createDOMPurify(window)
    instance.addHook('afterSanitizeAttributes', (node) => {
        if (!(node instanceof Element)) return
        const tag = node.tagName.toLowerCase()
        const allowed = RICH_TEXT_ALLOWED_ATTRIBUTES[tag] ?? []
        for (const attr of Array.from(node.attributes)) {
            if (!allowed.includes(attr.name)) node.removeAttribute(attr.name)
        }
        if (node.hasAttribute('class')) {
            const keep = (node.getAttribute('class') ?? '')
                .split(/\s+/)
                .filter((c) => (RICH_TEXT_ALLOWED_CLASSES[tag] ?? []).includes(c))
            if (keep.length) node.setAttribute('class', keep.join(' '))
            else node.removeAttribute('class')
        }
        if (tag === 'a') {
            const href = node.getAttribute('href')
            if (href != null && !urlAllowed(href, RICH_TEXT_LINK_SCHEMES)) node.removeAttribute('href')
            if (node.hasAttribute('target')) node.setAttribute('target', '_blank')
            node.setAttribute('rel', RICH_TEXT_LINK_REL)
        }
        if (tag === 'img') {
            const src = node.getAttribute('src')
            if (src != null && !urlAllowed(src, RICH_TEXT_IMAGE_SCHEMES)) node.removeAttribute('src')
        }
    })
    purifier = instance
    return purifier
}

/**
 * Clean engineer- or manager-written HTML to the editor's own vocabulary, for rendering as HTML.
 * Anything that is not a string becomes ''. With no DOM to parse in (never in the browser), it
 * returns '' rather than the raw input - an unrendered update is safe, a raw one is not.
 */
export function sanitizeRichText(html: unknown): string {
    if (typeof html !== 'string' || html === '') return ''
    const instance = getPurifier()
    if (!instance) return ''
    return instance.sanitize(html, {
        ALLOWED_TAGS: [...RICH_TEXT_ALLOWED_TAGS],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'src', 'alt', 'title'],
        // The hook above decides schemes per tag; this only has to let candidates through to it.
        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
        ALLOW_DATA_ATTR: false,
        FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed', 'form', 'svg', 'math'],
        KEEP_CONTENT: true,
    })
}
